import { Track } from '@bariendo/lib/constants';
import { getPatientTrack } from '@bariendo/lib/episode-of-care';
import { useMedplum } from '@medplum/react';
import { useCallback, useEffect, useState } from 'react';

/** Every mounted `usePatientTrack`, so a switch made anywhere re-reads them all. */
const trackListeners = new Set<(patientId: string) => void>();

/**
 * Tells every `usePatientTrack` reading this patient that the track changed,
 * so the header pill, Intake and billing and the PN card re-read it together.
 * Call after a successful switch; a single instance's `reload` refreshes only
 * itself.
 */
export function announcePatientTrackChange(patientId: string): void {
  trackListeners.forEach((listener) => listener(patientId));
}

/**
 * Resolve a patient's acquisition track (GTM channel) from their active
 * `EpisodeOfCare` in Medplum (BAR-2335). Async — the track lives on the episode,
 * not on the loaded Patient, so this fires a search. `undefined` means the track
 * could not be resolved (no active episode) — callers treat that as the default
 * self-pay / D2C track.
 *
 * `reload` re-reads the episode. A track switch closes the old `EpisodeOfCare`
 * and opens a new one, so the resolved value changes without anything on the
 * loaded Patient changing — nothing else on the page would trigger a refetch.
 */
export function usePatientTrack(patientId?: string): {
  track: Track | undefined;
  loading: boolean;
  reload: () => Promise<void>;
} {
  const medplum = useMedplum();
  const [track, setTrack] = useState<Track | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(Boolean(patientId));
  // Bumped by `reload` to re-run the effect below, so the fetch and its
  // cancellation stay in one place rather than being duplicated.
  const [reloadToken, setReloadToken] = useState(0);

  const reload = useCallback(async (): Promise<void> => {
    setReloadToken((token) => token + 1);
  }, []);

  useEffect(() => {
    if (!patientId) return undefined;
    const listener = (changed: string) => {
      if (changed === patientId) setReloadToken((token) => token + 1);
    };
    trackListeners.add(listener);
    return () => {
      trackListeners.delete(listener);
    };
  }, [patientId]);

  useEffect(() => {
    if (!patientId) {
      setTrack(undefined);
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    getPatientTrack(medplum, patientId)
      .then((resolved) => {
        if (active) {
          setTrack(resolved);
        }
      })
      .catch(() => {
        if (active) {
          setTrack(undefined);
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [medplum, patientId, reloadToken]);

  return { track, loading, reload };
}
